import React, { useState, useEffect } from 'react';

import { Grid, Typography, List, ListItem, ListItemText, Box, Link } from '@mui/material';
import { styled } from '@mui/system';
import GradientCircularProgress from '../components/common/GradientCircularProgress';
import ActionButton from '../components/common/buttons/ActionButton';
import { formatDate } from '../utils.js';
import { DEFAULT_PRODUCT_HANDLE } from '../constants/shop';
import { getProduct } from './utils';
import { OrderData as OrderDataType, TestKitDataType } from '../types/orderTypes';
import { useNavigate } from 'react-router-dom';
import { useThemeContext } from '../contexts/ThemeContext';

const BoxHeaderStyled = styled(Box)(({ theme }) => ({
  display: 'flex',
  [theme.breakpoints.down('sm')]: {
    display: 'block',
    textAlign: 'center',
  },
  marginTop: '11px',
  justifyContent: 'space-between',
  margin: '0 17px',
  border: 'solid 1px',
  padding: '13px',
}));

const ListContainerStyled = styled(List)(({ theme }) => ({
  margin: '0 17px',
  padding: '13px',
  [theme.breakpoints.down('sm')]: {
    '& .MuiListItem-root': {
      display: 'block',
      textAlign: 'center',
    },
  },
}));

const TrackingInformationBoxStyled = styled(List)(({ theme }) => ({
  margin: '0 17px 20px 0px',
  padding: '0px 0px 13px 0px',
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'left',
  [theme.breakpoints.down('sm')]: {
    display: 'block',
    textAlign: 'left',
  },
}));

const TitleTypography = styled(Typography)({
  fontSize: '1.25rem',
  marginBottom: '25px',
  textAlign: 'center',
});

const TitleHeaderSections = styled(Typography)({
  fontSize: '1.1rem',
});

type OrderDataProps = {
  orderData: OrderDataType;
  testKitsData?: TestKitDataType[];
};

const OrderDetailsContent: React.FC<OrderDataProps> = ({ orderData, testKitsData }) => {
  const [productImageUrl, setProductImageUrl] = useState<string | null>('');
  const { userType } = useThemeContext();
  const navigate = useNavigate();

  useEffect(() => {
    const fetchProductImage = async () => {
      const sProduct = await getProduct(DEFAULT_PRODUCT_HANDLE);
      const featuredImageUrl = sProduct?.featuredImage?.url;
      setProductImageUrl(featuredImageUrl);
    };
    fetchProductImage();
  }, []);

  if (!orderData) return null;

  const renderHeaderInformation = () => {
    return (
      <Grid item xs={12} sx={{ mt: 3 }}>
        <TitleTypography>Order Information</TitleTypography>
        <BoxHeaderStyled>
          <Box>
            <TitleHeaderSections>Order: {orderData.order_name}</TitleHeaderSections>
            <Typography>Placed on: {formatDate(orderData.order_date)}</Typography>
            <TitleHeaderSections>Total: ${orderData.cost?.total_amount?.amount}</TitleHeaderSections>
          </Box>
          <Box>
            <TitleHeaderSections>Shipping Address</TitleHeaderSections>
            <Typography>{orderData.shipping_address.address1}</Typography>
            <Typography>{orderData.shipping_address.address2}</Typography>
            <Typography>
              {orderData.shipping_address.city} {orderData.shipping_address.state}&nbsp;&nbsp;
              {orderData.shipping_address.zipcode}
            </Typography>
          </Box>
        </BoxHeaderStyled>
      </Grid>
    );
  };

  const renderProductImageUrl = () => {
    if (!productImageUrl) {
      return <GradientCircularProgress loadingMessage={''} />;
    }

    return <img style={{ width: '77px' }} src={productImageUrl} alt={productImageUrl} loading="lazy" />;
  };

  const renderTestKitImage = () => {
    return (
      <Box
        sx={(theme) => ({
          [theme.breakpoints.down('sm')]: {
            width: 'auto',
          },
          width: '35%',
        })}
      >
        {renderProductImageUrl()}
      </Box>
    );
  };

  const renderActivationButton = (testKitDetails: TestKitDataType) => {
    if (!testKitDetails.sample_id) return;

    if (testKitDetails.activated)
      return (
        <Typography color="textSecondary" sx={{ marginTop: 1, fontSize: '0.9em', width: '175px' }}>
          &nbsp;
        </Typography>
      );

    let activateLink = `/activate.html?activationCode=${testKitDetails.sample_id}`;
    if (userType === 'unauthenticated') {
      activateLink = `/guest-activate.html?activationCode=${testKitDetails.sample_id}`;
    }

    return (
      <ActionButton customStyles={{ width: '175px' }} label="Activate Kit" onClick={() => navigate(activateLink)} />
    );
  };

  const renderTextKitSubjectName = (testKit: TestKitDataType, index: number) => {
    let finalSubjectName = testKit.subject_name;
    if (!testKit.subject_name) finalSubjectName = `Test Kit #${index + 1}`;

    return (
      <Grid item xs={12}>
        <ListItemText primary={finalSubjectName} />
        <Typography color="textSecondary" sx={{ marginTop: 1, fontSize: '0.9em' }}>
          Activation: {testKit.activated ? 'Done' : 'Pending'}
        </Typography>
      </Grid>
    );
  };

  const renderTrackingLink = (tracking_url: string, linkAvailableText: string, linkNotAvailableText: string) => {
    return (
      <>
        {tracking_url ? (
          <Typography
            sx={{
              color: '#03DAB2',
              fontSize: '14px',
              fontStyle: 'normal',
              fontWeight: '400',
              lineHeight: '20px',
              letterSpacing: '0.25px',
            }}
          >
            <Link href={tracking_url} target="_blank" color="inherit" underline="none" style={{ width: '175px' }}>
              {linkAvailableText}
            </Link>
          </Typography>
        ) : (
          <Typography
            color="textSecondary"
            sx={{
              marginTop: 0,
              fontSize: '14px',
              fontStyle: 'normal',
              fontWeight: '400',
              lineHeight: '20px',
              letterSpacing: '0.25px',
            }}
          >
            {linkNotAvailableText}
          </Typography>
        )}
      </>
    );
  };

  const renderTrackingInformationSection = (testKit: TestKitDataType) => {
    return (
      <Grid item xs={12} sx={{ paddingTop: '0px !important' }}>
        <TrackingInformationBoxStyled>
          <Box
            sx={(theme) => ({
              [theme.breakpoints.down('sm')]: {
                textAlign: 'center',
                marginTop: 4,
              },
              textAlign: 'left',
            })}
          >
            {renderTrackingLink(
              testKit.tracking_info?.in_transit_to_patient_url || '',
              'Track kit to User',
              'Tracking kit to user not available.',
            )}
            {renderTrackingLink(
              testKit.tracking_info?.in_transit_to_lab_url || '',
              'Track kit to Lab',
              'Tracking kit to Lab not available.',
            )}
          </Box>
        </TrackingInformationBoxStyled>
      </Grid>
    );
  };

  const renderTestKit = (testKit: TestKitDataType, index: number) => {
    return (
      <Grid item xs={12} sx={{ paddingTop: '0px !important' }}>
        {renderTextKitSubjectName(testKit, index)}
        {renderTrackingInformationSection(testKit)}
      </Grid>
    );
  };

  const renderTestSubjectsSection = () => {
    return (
      <Grid item xs={12}>
        <TitleTypography>Test Kits</TitleTypography>
        <ListContainerStyled>
          {testKitsData?.map((testKit, index) => (
            <ListItem sx={{ pb: 2, mb: 2, borderBottom: '1px solid' }} key={`${index} - ${testKit.test_kit_id}`}>
              {renderTestKitImage()}
              {renderTestKit(testKit, index)}
              {renderActivationButton(testKit)}
            </ListItem>
          ))}
        </ListContainerStyled>
      </Grid>
    );
  };

  return (
    <Grid container spacing={3}>
      {renderHeaderInformation()}
      {renderTestSubjectsSection()}
    </Grid>
  );
};

export default OrderDetailsContent;
